import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Grid,
} from '@mui/material';
import { validateRetrieveCode } from '../services/contractorJobService';

export const ContractorRetrieveCode: React.FC = () => {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const [retrieveCode, setRetrieveCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobData, setJobData] = useState<any>(null);
  const [contractorStatus, setContractorStatus] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!retrieveCode || !/^\d{4}$/.test(retrieveCode)) {
      setError('Please enter a valid 4-digit retrieve code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await validateRetrieveCode(retrieveCode);
      setJobData(response);
      
      // Redirect to job portal (handles both registered and unregistered contractors)
      // If not registered, they'll be prompted to set up their account on the job portal
      navigate(`/contractor/job/${jobId}/${retrieveCode}`);
      return;
      
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.contractorStatus) {
        setContractorStatus(errorData.contractorStatus);
        setError(errorData.message || errorData.error);
      } else {
        setError(errorData?.error || 'Invalid retrieve code');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Contractor Job Portal
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Enter your 4-digit retrieve code to access your job
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your retrieve code was sent to you via email. Please check your inbox for the code.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Retrieve Code"
                  value={retrieveCode}
                  onChange={(e) => setRetrieveCode(e.target.value)}
                  placeholder="1234"
                  inputProps={{ 
                    maxLength: 4,
                    pattern: '[0-9]{4}',
                    style: { 
                      textAlign: 'center', 
                      fontSize: '1.8rem', 
                      letterSpacing: '0.8rem',
                      fontWeight: 'bold'
                    }
                  }}
                  error={!!error}
                  helperText={error || "Enter the 4-digit code from your email"}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& input': {
                        textAlign: 'center',
                        fontSize: '1.8rem',
                        letterSpacing: '0.8rem',
                        fontWeight: 'bold'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || retrieveCode.length !== 4}
                  sx={{ py: 1.5 }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Validating...
                    </>
                  ) : (
                    'Access Job'
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>

          {jobData && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="info">
                <Typography variant="body2">
                  Job found for {jobData.contractorAccount?.email}
                </Typography>
              </Alert>
            </Box>
          )}

          {contractorStatus === 'not_selected' && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="info" sx={{ textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Thank You for Your Interest
                </Typography>
                <Typography variant="body2">
                  Unfortunately, another contractor has been selected for this project. 
                  We appreciate your time and will keep your information on file for future opportunities.
                </Typography>
              </Alert>
            </Box>
          )}

          {contractorStatus === 'cancelled' && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="warning" sx={{ textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Job Cancelled
                </Typography>
                <Typography variant="body2">
                  This job has been cancelled and is no longer available.
                </Typography>
              </Alert>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ContractorRetrieveCode;
